import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { SessionTeardownService } from '@backbase/shared/feature/auth';
import {
  LoanApplicationReviewData,
  LoanLeadData,
  LoanRepaymentReviewData,
  LoanTopUpReviewData
} from '../../../sbk-loans/utils/loan-types';
import { UnitTrustAccountDraft } from '../../../invest/utils/unit-trust-review.utils';

export interface TransferFee {
  amount: string;
  currencyCode: string;
}

export interface TransferAdditions {
  cutOffTime: string;
  errorMessage?: string;
}

export interface OpenSavingsAccountDraft {
  sourceOfFunds?: string;
  purpose?: string;
  accountType?: string;
  accountCurrency?: string;
  accountName?: string;
  accountId?: string;
}

export interface UnitTrustAccountData extends UnitTrustAccountDraft {
  fundCode: string;
}

export interface TransferEntity {
  [key: string]: unknown;
}

export interface TransferPaymentData extends TransferEntity {
  paymentType?: string;
  chargeBearer?: string;
  initiator?: {
    counterPartyType?: string;
    fundingSource?: string;
    phoneNumber?: string;
  };
  counterparty?: {
    counterPartyType?: string;
    networkProvider?: string;
    chinaPurposeCodes?: string;
    uaePurposeCodes?: string;
    accountNumber?: string;
    bankName?: string;
    bankBranchCode?: string;
    beneficiaryAddress?: string;
    beneficiaryStreet?: string;
    beneficiaryCntryTown?: string;
    postalAddress?: {
      beneficiaryCntryTown?: string;
      beneficiaryStreet?: string;
      addressLine1?: string;
      addressLine2?: string;
      addressLine3?: string;
    };
    tillNumber?: string;
    paybillNumber?: string;
    organizationName?: string;
    name?: string;
    phoneNumber?: string;
    topUpType?: string;
    obuCardNumber?: string;
    mtcCardNumber?: string;
    plateNumber?: string;
    paymentKey?: string;
    clientIdentifier?: string;
    accountName?: string;
    rtgsCode?: {
      rtgsCodeLabel?: string;
    };
  };
  remittanceInfo?: {
    remittanceInformation?: string;
  };
}

export interface FixedIncomeFundKesData extends TransferEntity {
  paymentKey?: string;
}

export interface WithdrawUnitTrustData extends TransferEntity {
  fromAccountId?: string;
  toAccountId?: string;
  amount?: string | number;
  currency?: string;
}

export interface TreasuryData extends TransferEntity {
  toAccountId?: string;
  accountId?: string;
  amount?: string | number;
  accountCurrency?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SbkTransfersStorageService {
  private paymentData = new Subject<TransferPaymentData>();
  private loanPaymentData = new Subject<LoanRepaymentReviewData | null>();
  private loanApplicationData = new Subject<LoanApplicationReviewData | null>();
  private carLoanApplicationData = new Subject<LoanLeadData | null>();
  private homeLoanApplicationData = new Subject<LoanLeadData | null>();
  private creditCardApplicationData = new Subject<LoanLeadData | null>();
  private openSavingsAccountData = new Subject<OpenSavingsAccountDraft | null>();
  private createMoneyMarketFundAccountData = new Subject<TransferEntity | null>();
  private createFixedIncomeFundAccountData = new Subject<TransferEntity | null>();
  private fundUnitTrustAccountData = new Subject<TransferEntity | null>();
  private withdrawFromUnitTrustAccountData = new Subject<WithdrawUnitTrustData | null>();
  private loanTopUpData = new Subject<LoanTopUpReviewData | null>();
  private treasuryData = new Subject<TreasuryData | null>();
  private transferFee = new BehaviorSubject<TransferFee>({ amount: '0', currencyCode: 'KES' });
  private additions = new BehaviorSubject<TransferAdditions>({ cutOffTime: '' });
  private transferStatus = new Subject<TransferEntity>();
  private transferResponse = new Subject<TransferEntity>();
  private paymentOrderStatus = new Subject<TransferEntity>();
  private paymentOrderResponse = new Subject<TransferEntity>();
  private initiatorAccount = new Subject<TransferEntity>();
  private amountGroup = new Subject<TransferEntity>();
  private conversion = new Subject<TransferEntity>();
  private currencies = new Subject<string[]>();
  private paymentFormSubmitted = new Subject<boolean>();

  paymentDataObj: TransferPaymentData | null = null;
  loanPaymentDataObj: LoanRepaymentReviewData | null = null;
  loanApplicationDataObj: LoanApplicationReviewData | null = null;
  carLoanApplicationDataObj: LoanLeadData | null = null;
  homeLoanApplicationDataObj: LoanLeadData | null = null;
  creditCardApplicationDataObj: LoanLeadData | null = null;
  openSavingsAccountDataObj: OpenSavingsAccountDraft | null = null;
  loanTopUpDataObj: LoanTopUpReviewData | null = null;
  createMoneyMarketFundAccountDataObj: TransferEntity | null = null;
  createFixedIncomeFundAccountDataObj: TransferEntity | null = null;
  createFixedIncomeFundUsdAccountDataObj: TransferEntity | null = null;
  createFixedIncomeFundKesAccountDataObj: FixedIncomeFundKesData | null = null;
  fundUnitTrustAccountDataObj: TransferEntity | null = null;
  withdrawFromUnitTrustAccountDataObj: WithdrawUnitTrustData | null = null;
  treasuryDataObj: TreasuryData | null = null;
  currenciesObj: string[] = [];

  private unitTrustAccountDataMap = new Map<string, UnitTrustAccountData>();

  constructor() {
    // Every `*Obj` and the `unitTrustAccountDataMap` cached above contain
    // user-scoped data (payment amounts, account ids, beneficiary names,
    // loan applications, fund identifiers). Clear them on session teardown
    // so nothing survives logout on a shared device.
    const sessionTeardown = inject(SessionTeardownService);
    const destroyRef = inject(DestroyRef);
    sessionTeardown.cleared$
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => this.clearAll());
  }

  /**
   * Resets every cached `*Obj` and the unit-trust account map. Called
   * automatically on session teardown, but can also be invoked manually
   * (e.g. when a journey explicitly wants a fresh slate).
   */
  clearAll(): void {
    this.paymentDataObj = null;
    this.loanPaymentDataObj = null;
    this.loanApplicationDataObj = null;
    this.carLoanApplicationDataObj = null;
    this.homeLoanApplicationDataObj = null;
    this.creditCardApplicationDataObj = null;
    this.openSavingsAccountDataObj = null;
    this.loanTopUpDataObj = null;
    this.createMoneyMarketFundAccountDataObj = null;
    this.createFixedIncomeFundAccountDataObj = null;
    this.createFixedIncomeFundUsdAccountDataObj = null;
    this.createFixedIncomeFundKesAccountDataObj = null;
    this.fundUnitTrustAccountDataObj = null;
    this.withdrawFromUnitTrustAccountDataObj = null;
    this.treasuryDataObj = null;
    this.currenciesObj = [];
    this.unitTrustAccountDataMap.clear();
  }

  setPaymentData(payment: TransferPaymentData) {
    this.paymentDataObj = payment;
    this.paymentData.next(payment);
  }

  getPaymentData(): Observable<TransferPaymentData> {
    return this.paymentData.asObservable();
  }

  setLoanPaymentData(payment: LoanRepaymentReviewData) {
    this.loanPaymentDataObj = payment;
    this.loanPaymentData.next(payment);
  }

  resetLoanPaymentData() {
    this.loanPaymentDataObj = null;
    this.loanPaymentData.next(null);
  }

  getLoanPaymentData(): Observable<LoanRepaymentReviewData | null> {
    return this.loanPaymentData.asObservable();
  }

  setOpenSavingsAccountData(data: OpenSavingsAccountDraft) {
    this.openSavingsAccountDataObj = data;
    this.openSavingsAccountData.next(data);
  }

  setLoanApplicationData(data: LoanApplicationReviewData) {
    this.loanApplicationDataObj = data;
    this.loanApplicationData.next(data);
  }

  resetLoanApplicationData() {
    this.loanApplicationDataObj = null;
    this.loanApplicationData.next(null);
  }

  setCarLoanApplicationData(data: LoanLeadData) {
    this.carLoanApplicationDataObj = data;
    this.carLoanApplicationData.next(data);
  }

  resetCarLoanApplicationData() {
    this.carLoanApplicationDataObj = null;
    this.carLoanApplicationData.next(null);
  }

  setHomeLoanApplicationData(data: LoanLeadData) {
    this.homeLoanApplicationDataObj = data;
    this.homeLoanApplicationData.next(data);
  }

  resetHomeLoanApplicationData() {
    this.homeLoanApplicationDataObj = null;
    this.homeLoanApplicationData.next(null);
  }

  setCreditCardApplicationData(data: LoanLeadData) {
    this.creditCardApplicationDataObj = data;
    this.creditCardApplicationData.next(data);
  }

  resetCreditCardApplicationData() {
    this.creditCardApplicationDataObj = null;
    this.creditCardApplicationData.next(null);
  }

  resetOpenSavingsAccountData() {
    this.openSavingsAccountDataObj = null;
    this.openSavingsAccountData.next(null);
  }

  setCreateMoneyMarketFundAccountData(data: TransferEntity) {
    this.createMoneyMarketFundAccountDataObj = data;
    this.createMoneyMarketFundAccountData.next(data);
  }

  resetCreateMoneyMarketFundAccountData() {
    this.createMoneyMarketFundAccountDataObj = null;
    this.createMoneyMarketFundAccountData.next(null);
  }

  setCreateFixedIncomeFundAccountData(data: TransferEntity) {
    this.createFixedIncomeFundAccountDataObj = data;
    this.createFixedIncomeFundAccountData.next(data);
  }

  resetCreateFixedIncomeFundAccountData() {
    this.createFixedIncomeFundAccountDataObj = null;
    this.createFixedIncomeFundAccountData.next(null);
  }

  setCreateFixedIncomeFundUsdAccountData(data: TransferEntity) {
    this.createFixedIncomeFundUsdAccountDataObj = data;
  }

  resetCreateFixedIncomeFundUsdAccountData() {
    this.createFixedIncomeFundUsdAccountDataObj = null;
  }

  setCreateFixedIncomeFundKesAccountData(data: FixedIncomeFundKesData) {
    this.createFixedIncomeFundKesAccountDataObj = data;
  }

  resetCreateFixedIncomeFundKesAccountData() {
    this.createFixedIncomeFundKesAccountDataObj = null;
  }

  setFundUnitTrustAccountData(data: TransferEntity) {
    this.fundUnitTrustAccountDataObj = data;
    this.fundUnitTrustAccountData.next(data);
  }

  resetFundUnitTrustAccountData() {
    this.fundUnitTrustAccountDataObj = null;
    this.fundUnitTrustAccountData.next(null);
  }

  setTreasuryData(data: TreasuryData) {
    this.treasuryDataObj = data;
    this.treasuryData.next(data);
  }

  resetTreasuryData() {
    this.treasuryDataObj = null;
    this.treasuryData.next(null);
  }

  setWithdrawFromUnitTrustAccountData(data: WithdrawUnitTrustData) {
    this.withdrawFromUnitTrustAccountDataObj = data;
    this.withdrawFromUnitTrustAccountData.next(data);
  }

  resetWithdrawFromUnitTrustAccountData() {
    this.withdrawFromUnitTrustAccountDataObj = null;
    this.withdrawFromUnitTrustAccountData.next(null);
  }

  getLoanApplicationData(): Observable<LoanApplicationReviewData | null> {
    return this.loanApplicationData.asObservable();
  }

  setLoanTopUpData(data: LoanTopUpReviewData) {
    this.loanTopUpDataObj = data;
    this.loanTopUpData.next(data);
  }

  resetLoanTopUpData() {
    this.loanTopUpDataObj = null;
    this.loanTopUpData.next(null);
  }

  getLoanTopUpData(): Observable<LoanTopUpReviewData | null> {
    return this.loanTopUpData.asObservable();
  }

  setTransferFee(transferFee: TransferFee) {
    this.transferFee.next(transferFee);
  }

  getTransferFee(): Observable<TransferFee> {
    return this.transferFee.asObservable();
  }

  setAdditions(additions: TransferAdditions) {
    this.additions.next(additions);
  }

  getAdditions(): Observable<TransferAdditions> {
    return this.additions.asObservable();
  }

  setTransferStatus(transferStatus: TransferEntity) {
    this.transferStatus.next(transferStatus);
  }

  getTransferStatus(): Observable<TransferEntity> {
    return this.transferStatus.asObservable();
  }

  setTransferResponse(transferResponse: TransferEntity) {
    this.transferResponse.next(transferResponse);
  }

  getTransferResponse(): Observable<TransferEntity> {
    return this.transferResponse.asObservable();
  }

  setPaymentOrderStatus(paymentOrderStatus: TransferEntity) {
    this.paymentOrderStatus.next(paymentOrderStatus);
  }

  getPaymentOrderStatus(): Observable<TransferEntity> {
    return this.paymentOrderStatus.asObservable();
  }

  setPaymentOrderResponse(paymentOrderResponse: TransferEntity) {
    this.paymentOrderResponse.next(paymentOrderResponse);
  }

  getPaymentOrderResponse(): Observable<TransferEntity> {
    return this.paymentOrderResponse.asObservable();
  }

  setInitiatorAccount(initiatorAccount: TransferEntity) {
    this.initiatorAccount.next(initiatorAccount);
  }

  getInitiatorAccount(): Observable<TransferEntity> {
    return this.initiatorAccount.asObservable();
  }

  setAmountGroup(amountGroup: TransferEntity) {
    this.amountGroup.next(amountGroup);
  }

  getAmountGroup(): Observable<TransferEntity> {
    return this.amountGroup.asObservable();
  }

  setConversion(conversion: TransferEntity) {
    this.conversion.next(conversion);
  }

  getConversion(): Observable<TransferEntity> {
    return this.conversion.asObservable();
  }

  setCurrencies(currencies: string[]) {
    this.currenciesObj = currencies;
    this.currencies.next(currencies);
  }

  getCurrencies(): Observable<string[]> {
    return this.currencies.asObservable();
  }

  setSubmittedStatus(isSubmitted: boolean) {
    this.paymentFormSubmitted.next(isSubmitted);
  }

  getSubmittedStatus(): Observable<boolean> {
    return this.paymentFormSubmitted.asObservable();
  }

  setUnitTrustAccountData(data: UnitTrustAccountData): void {
    this.unitTrustAccountDataMap.set(data.fundCode, data);
  }

  getUnitTrustAccountData(fundCode: string): UnitTrustAccountData | undefined {
    return this.unitTrustAccountDataMap.get(fundCode);
  }

  resetUnitTrustAccountData(fundCode: string): void {
    this.unitTrustAccountDataMap.delete(fundCode);
  }

  resetAllUnitTrustAccountData(): void {
    this.unitTrustAccountDataMap.clear();
  }
}
